import type { Branding } from '~/helpers/branding/branding-context'
import { useQuery } from '@apollo/client/react'
import { deriveSidebarAccent, effectiveInk, readableTextOn, TEXT_SURFACES } from '@studeva/brand-contrast'
import { useEffect, useMemo } from 'react'
import { GQL_APP_CONFIG } from '~/gql/app-config/app-config'
import { BrandingContext, DEFAULT_BRANDING } from '~/helpers/branding/branding-context'
import { applyFavicon } from '~/helpers/branding/favicon'
import { useOrganizationLanguage } from '~/i18n/use-organization-language'

/** Applique une variable CSS sur <html> ou la retire si la valeur est vide. */
function applyCssVar(name: string, value?: string | null) {
  const root = document.documentElement
  if (value) root.style.setProperty(name, value)
  else root.style.removeProperty(name)
}

/**
 * Récupère la configuration white-label (query publique, dispo avant login) et
 * l'applique au runtime : titre de l'onglet, favicon, tokens de couleur/typo.
 * Expose le branding aux composants via useBranding().
 *
 * Ce fichier n'exporte QUE ce composant : le contexte vit dans
 * `branding-context.ts` et `applyFavicon` dans `favicon.ts`. Mêler un composant
 * et des fonctions dans un même module coupe le Fast Refresh de Vite — toute
 * retouche ici rechargeait la page entière au lieu du seul composant.
 */
export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { data, loading } = useQuery(GQL_APP_CONFIG)

  const branding = useMemo<Branding>(
    () => ({
      ...DEFAULT_BRANDING,
      ...(data?.appConfig ?? {}),
      // `!loading` couvre l'échec de la query : sans ça, une org dont le branding
      // ne répond pas ne verrait jamais la notice RGPD qui attend ce drapeau.
      loaded: Boolean(data?.appConfig) || !loading,
    }),
    [data, loading],
  )

  useOrganizationLanguage(branding.defaultLocale)

  useEffect(() => {
    applyFavicon(branding.favicon)
    /*
     * Le jeton de FOND et celui de TEXTE se posent ensemble, toujours.
     *
     * Les thèmes définissent un couple cohérent — fond noir / texte blanc en
     * clair, l'inverse en sombre. Le branding ne remplaçait que le fond : une
     * organisation en bleu nuit héritait du texte noir du thème sombre, et son
     * bouton principal devenait illisible. Le défaut existait dans les deux
     * sens, une marque claire héritant du texte blanc du thème clair.
     *
     * L'encre est CHOISIE par l'organisation, et calculée seulement à défaut :
     * une marque a souvent une encre imposée par sa charte, que la déduction
     * remplaçait par un blanc ou un noir corrects mais étrangers. Le repli
     * garantit qu'aucune org ne se retrouve sans encre le jour où elle change
     * sa couleur sans toucher au reste.
     */
    const primaryInk = effectiveInk(branding.primaryColor, branding.primaryForeground)
    applyCssVar('--color-primary', branding.primaryColor)
    applyCssVar('--color-primary-foreground', primaryInk)
    // La barre latérale porte la primaire de l'organisation (#79). Sans
    // personnalisation, elle garde la couleur de barre du thème, distincte de
    // l'ambre des boutons.
    applyCssVar('--brand-bar', branding.primaryColor)
    applyCssVar('--brand-bar-ink', branding.primaryColor && primaryInk)
    // La marque en TEXTE, une teinte par thème : une seule valeur ne peut pas
    // être lisible à la fois sur le fond clair et sur le fond sombre.
    applyCssVar('--brand-text-light', branding.primaryColor && readableTextOn(branding.primaryColor, TEXT_SURFACES.light))
    applyCssVar('--brand-text-dark', branding.primaryColor && readableTextOn(branding.primaryColor, TEXT_SURFACES.dark))
    // Item actif de la barre latérale, posée sur la primaire (#79) : calculé
    // ici parce que le sens du glissement dépend de l'écart primaire / encre,
    // ce que CSS ne sait pas mesurer.
    applyCssVar('--sidebar-accent', branding.primaryColor && primaryInk
      ? deriveSidebarAccent(branding.primaryColor, primaryInk)
      : null)
    applyCssVar('--color-accent', branding.accentColor)
    applyCssVar('--color-accent-foreground', effectiveInk(branding.accentColor, branding.accentForeground))
    // Plus de police par organisation (#31) : la typographie est celle du
    // produit, définie dans le thème. Laisser un organisme saisir un nom de
    // famille ne garantissait pas qu'elle soit chargée — le navigateur
    // retombait en silence sur autre chose.
  }, [branding])

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  )
}
